import React, { useState, useEffect } from 'react'
import Plot from 'react-plotly.js'
import { Select } from 'antd'
import axios from 'axios'
import { useSelectedWeek } from '../../../../../../contexts/SelectedWeekContext'
import { computeSelectedWeekForSeason, getWeekFromWeek, getYearFromWeek, SeasonRange, seasonReferenceYear } from '../../../../../../../_metronic/helpers'

const { Option } = Select
interface DataItem {
  week: string
  nCasesNowcast: number | null
  lowerCiBaselineNCases: number | null
  upperCiBaselineNCases: number | null
}

interface Trace {
  x: string[]
  y: (number | null)[]
  type: string
  mode: string
  marker?: { color: string }
  line?: { color: string }
  yaxis?: string
  name: string
  hovertemplate: any | null
}
interface StreamgraphChartProps {
  season: SeasonRange
  selectedAgeClass: string[]
  setSelectedAgeClass: (v: any) => void
  selectedDiagnosisCategories: string
  setSelectedDiagnosisCategories: (v: any) => void
}
const VIRUS_LABELS = {
  all_resp_diags: "All virus",
  FLU: "Influenza",
  "COVID-19": "SARS-CoV-2",
  RSV: "RSV",
  RI_OTHER_VIRUS: "Other respiratory infections",
};
const StreamgraphChart: React.FC<StreamgraphChartProps> = ({ season, selectedAgeClass, setSelectedAgeClass, selectedDiagnosisCategories, setSelectedDiagnosisCategories }) => {
  const { selectedWeek, setSelectedWeek } = useSelectedWeek('2025-W40')
  const [data, setData] = useState<DataItem[]>([])
  const [diagnosisCategories, setDiagnosisCategories] = useState([])
  // const [selectedDiagnosisCategories, setSelectedDiagnosisCategories] =
  //   useState<string>('all_resp_diags')
  const [ageClass, setAgeClass] = useState([])
  // const [selectedAgeClass, setSelectedAgeClass] = useState<string[]>(['0-200'])
  const [showArrow, setShowArrow] = useState<boolean>(false)
  const [plotData, setPlotData] = useState<Trace[]>([])
  const API_URL = process.env.REACT_APP_API_URL
  const [seasonSelectedWeek, setSeasonSelectedWeek] = useState<string | null>(null)
  const startYear = getYearFromWeek(season.startWeek)
  const endYear = getYearFromWeek(season.endWeek)
  const startWeekNumber = getWeekFromWeek(season.startWeek) + 1

  function parseAgeClasses(ageClasses: string[]): string {
    const parsedString = ageClasses.map((age) => `ageClass%5B%5D=${age}`).join('&')
    return parsedString
  }
  const getVirusLabel = (value) => {
    return VIRUS_LABELS[value] || "";
  };
  function addMissingWeek(data: DataItem[]): DataItem[] {
    const startWeek = season.startWeek
    const endWeek = season.endWeek // Last week in the data
    const existingWeeks = new Set(data.map((item) => item.week)) // Set to store existing weeks
    const newData: DataItem[] = []

    let currentWeek = startWeek

    while (currentWeek !== endWeek) {
      // Move to the next week
      const [year, weekNum] = currentWeek.split('-W')
      const weekNumber = parseInt(weekNum)
      if (weekNumber < 52) {
        currentWeek = `${year}-W${(weekNumber + 1).toString()}`
      } else {
        currentWeek = `${parseInt(year) + 1}-W1`
      }
      if (!existingWeeks.has(currentWeek)) {
        newData.push({
          week: currentWeek,
          nCasesNowcast: null,
          lowerCiBaselineNCases: null,
          upperCiBaselineNCases: null,
        })
      }
    }

    // Find the index to insert the new week
    let insertionIndex = 0
    for (let i = 0; i < data.length; i++) {
      if (data[i].week <= `${startYear}-W52`) {
        insertionIndex = i + 1
      } else {
        break
      }
    }
    // Merge existing data with the newly added missing weeks
    const mergedData = [
      ...data.slice(0, insertionIndex),
      ...newData,
      ...data.slice(insertionIndex),
    ].sort((a, b) => {
      const aYear = parseInt(a.week.substring(0, 4), 10)
      const aWeek = parseInt(a.week.substring(a.week.indexOf('-W') + 2), 10)
      const bYear = parseInt(b.week.substring(0, 4), 10)
      const bWeek = parseInt(b.week.substring(b.week.indexOf('-W') + 2), 10)
      if (aYear !== bYear) {
        return aYear - bYear
      } else {
        return aWeek - bWeek
      }
    })

    return mergedData
  }
  // function parseDiagnosisCategory(diagnosisCategory: string[]): string {
  //   const parsedString = diagnosisCategory.map((age) => `diagnosisCategory%5B%5D=${age}`).join('&')
  //   return parsedString
  // }
  const fetchData = async (
    ageClasses = selectedAgeClass,
    diagnosisCategories = selectedDiagnosisCategories
  ) => {
    try {
      const traces: Trace[] = []

      // Fetch data for each age class and generate traces
      await Promise.all(
        ageClasses.map(async (ageClass, index) => {
          // Pass index to get color from colors array
          let filterAge = parseAgeClasses([ageClass])

          const response = await axios.get(
            `${API_URL}/hospitalAdmissionsRespiratorySyndromes/list?${filterAge}&diagnosisCategory=${diagnosisCategories}`
          )
          let readableAge
          let selectedColor
          switch (ageClass) {
            case 'Less than 1 year':
              readableAge = 'Less than 1 year'
              selectedColor = '#792054'
              break
            case '[1 - 5[ year(s)':
              readableAge = '1 to 4 years'
              selectedColor = '#33FF57'
              break
            case '[5 - 20[ years':
              readableAge = '5 to 19 years'
              selectedColor = '#5733FF'
              break
            case '[20 - 50[ years':
              readableAge = '20 to 49 years'
              selectedColor = '#FF33DD'
              break
            case '[50 - 65[ years':
              readableAge = '50 to 64 years'
              selectedColor = '#33DDFF'
              break
            case '65 years and older':
              readableAge = '65 years and older'
              selectedColor = '#1E778D'
              break
            default:
              readableAge = 'All ages'
              selectedColor = '#203879'
              break
          }

          const trace: Trace = {
            x: addMissingWeek(response.data)
              .filter((entry) => {
                const currentYear = parseInt(entry.week.substring(0, 4), 10)
                const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
                return (
                  (currentYear === startYear && currentWeek >= startWeekNumber) ||
                  (currentYear === endYear && currentWeek <= 27) ||
                  (currentYear > startYear && currentYear < endYear)
                );
              })
              .map((entry) => entry.week),
            y: addMissingWeek(response.data)
              .filter((entry) => {
                const currentYear = parseInt(entry.week.substring(0, 4), 10)
                const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
                return (
                  (currentYear === startYear && currentWeek >= startWeekNumber) ||
                  (currentYear === endYear && currentWeek <= 27) ||
                  (currentYear > startYear && currentYear < endYear)
                );
              })
              .map((entry) => entry.nCasesNowcast),
            yaxis: 'y',
            type: 'scatter',
            mode: 'lines',
            line: { color: selectedColor }, // Assign color based on index
            name: `${readableAge} Cases - ${getVirusLabel(selectedDiagnosisCategories)}`, // Add age class to trace name
            hovertemplate:
              '<b>%{fullData.name}</b><br>' +
              'Week: %{x}<br>' +
              '%{fullData.name}: %{y}<extra></extra>',
          }

          traces.push(trace)
        })
      )
      const filterAge = parseAgeClasses(['0-200'])
      const response = await axios.get(
        `${API_URL}/hospitalAdmissionsRespiratorySyndromes/list?${filterAge}&diagnosisCategory=${diagnosisCategories}`
      )
      // let endWeek = response.data[response.data.length - 1].week.split('-W')
      let endWeek = season?.endWeek?.split('-W')
      const alignedWeek =
        startYear == seasonReferenceYear
          ? selectedWeek
          : computeSelectedWeekForSeason(selectedWeek, season.startWeek)
      const selectWeek = alignedWeek.split('-W')
      const showArrow =
        (Number(selectWeek[0]) > startYear ||
          (Number(selectWeek[0]) === startYear && Number(selectWeek[1]) >= startWeekNumber)) &&
        (Number(selectWeek[0]) < Number(endWeek[0]) ||
          (Number(selectWeek[0]) === Number(endWeek[0]) &&
            Number(selectWeek[1]) <= Number(endWeek[1])))
      setShowArrow(showArrow)
      setSeasonSelectedWeek(alignedWeek);

      const trace1 = {
        x: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
            return (
              (currentYear === startYear && currentWeek >= startWeekNumber) ||
              (currentYear === endYear && currentWeek <= 27) ||
              (currentYear > startYear && currentYear < endYear)
            );
          })
          .map((entry) => entry.week),
        y: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
            return (
              (currentYear === startYear && currentWeek >= startWeekNumber) ||
              (currentYear === endYear && currentWeek <= 27) ||
              (currentYear > startYear && currentYear < endYear)
            );
          })
          .map((entry) => entry.lowerCiBaselineNCases),

        type: 'scatter',
        mode: 'lines',
        //yaxis: 'y2',
        line: { color: 'lightgrey', dash: 'dashdot' },
        name: `min all ages - ${getVirusLabel(selectedDiagnosisCategories)}`, // Add age class to trace name
        showlegend: false,
        hovertemplate:
          '<b>%{fullData.name}</b><br>' +
          'Week: %{x}<br>' +
          '%{fullData.name}: %{y}<extra></extra>',
      }

      const trace2 = {
        x: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
            return (
              (currentYear === startYear && currentWeek >= startWeekNumber) ||
              (currentYear === endYear && currentWeek <= 27) ||
              (currentYear > startYear && currentYear < endYear)
            );
          })
          .map((entry) => entry.week),
        y: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
            return (
              (currentYear === startYear && currentWeek >= startWeekNumber) ||
              (currentYear === endYear && currentWeek <= 27) ||
              (currentYear > startYear && currentYear < endYear)
            );
          })
          .map((entry) => entry.upperCiBaselineNCases),
        fill: 'tonexty',
        type: 'scatter',
        //yaxis: 'y2',
        mode: 'lines',
        line: { color: '#BBBBBC', dash: 'dashdot' },
        name: `Statistical range of normal percentage* - ${getVirusLabel(selectedDiagnosisCategories)}`, // Add age class to trace name
        hovertemplate:
          '<b>%{fullData.name}</b><br>' +
          'Week: %{x}<br>' +
          '%{fullData.name}: %{y}<extra></extra>',
      }
      if (ageClasses.includes('0-200') && ageClasses.length <= 1) {
        traces.unshift(trace2)
        traces.unshift(trace1)
      }
      const startWeek = season.startWeek // First week in the data
      const endWeek2 = season.endWeek // Last week in the data
      const existingWeeks = new Set(response.data.map((item) => item.week)) // Set to store existing weeks
      const newData: DataItem[] = []
      const missingData: DataItem[] = []

      let currentWeek = startWeek

      while (currentWeek !== endWeek2) {
        // Move to the next week
        const [year, weekNum] = currentWeek.split('-W')
        const weekNumber = parseInt(weekNum)
        if (weekNumber < 52) {
          currentWeek = `${year}-W${(weekNumber + 1).toString()}`
        } else {
          currentWeek = `${parseInt(year) + 1}-W1`
        }
        if (!existingWeeks.has(currentWeek)) {
          missingData.push({
            week: currentWeek,
            nCasesNowcast: null,
            lowerCiBaselineNCases: null,
            upperCiBaselineNCases: null,
          })
        }
      }

      const trace3: Trace = {
        x: missingData
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
            return (
              (currentYear === startYear && currentWeek >= startWeekNumber) ||
              (currentYear === endYear && currentWeek <= 27) ||
              (currentYear > startYear && currentYear < endYear)
            );
          })
          .map((entry) => entry.week),
        y: missingData
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
            return (
              (currentYear === startYear && currentWeek >= startWeekNumber) ||
              (currentYear === endYear && currentWeek <= 27) ||
              (currentYear > startYear && currentYear < endYear)
            );
          })
          .map((entry) => 0),
        yaxis: 'y',
        type: 'scatter',
        mode: 'markers',
        marker: { color: '#000000' }, // Assign color based on index
        name: `Missing Data `, // Add age class to trace name
        hovertemplate:
          '<b>%{fullData.name}</b><br>' +
          'Week: %{x}<br>' +
          '%{fullData.name}: %{y}<extra></extra>',
      }
      traces.push(trace3)
      setPlotData(traces)
    } catch (error) {
      console.error('Error fetching data:', error)
      setData([])
    }
  }

  useEffect(() => {
    const listDiagnosisCategory = async () => {
      try {
        const response = await axios.get(
          API_URL + '/hospitalAdmissionsRespiratorySyndromes/listDiagnosisCategory'
        )
        const data = response.data.filter((category) => category !== 'IR GENERALE')
        setDiagnosisCategories(data)
      } catch (error) {
        console.error('Error fetching data:', error)
        return null
      }
    }

    const listAgeClass = async () => {
      try {
        const response = await axios.get(
          API_URL + '/hospitalAdmissionsRespiratorySyndromes/listAgeClass'
        )
        const data = response.data
        setAgeClass(data)
      } catch (error) {
        console.error('Error fetching data:', error)
        return null
      }
    }

    listDiagnosisCategory()
    listAgeClass()
    fetchData(selectedAgeClass, selectedDiagnosisCategories)
  }, [selectedAgeClass, selectedDiagnosisCategories, selectedWeek])

  // const trace3 = {
  //   x: data.map((entry) => entry.week),
  //   y: data.map((entry) => entry.nCases),
  //   type: 'bar',
  //   marker: {color: '#DBDFE9'},
  //   name: 'pas encore spécifié ',
  // }

  const layout = {
    title: 'Number of hospital admissions of patients with a diagnosis of respiratory syndromes',
    barmode: 'stack',
    bargap: 0.2, // Adjust the gap between bars
    bargroupgap: 0.1, // Adjust the gap between bar groups
    autosize: true,

    annotations: [
      {
        xref: 'paper',
        yref: 'paper',
        x: -0.035,
        xanchor: 'right',
        y: 0.5,
        yanchor: 'bottom',
        text: '#',
        font: {
          size: 25,

          color: '#203879',
        },
        showarrow: false,
      },
      {
        x: startYear == seasonReferenceYear ? selectedWeek : seasonSelectedWeek,
        y: 0,
        xref: 'x',
        yref: 'y',
        text: showArrow ? 'Selected Week' : '',
        font: {
          family: 'Courier New, monospace',
          size: 16,
          color: '#CC1B1B',
        },
        arrowcolor: '#CC1B1B',
        showarrow: showArrow,
        arrowhead: 7,
        ax: 0,
        ay: -200,
      },
      // {
      //   xref: 'paper',
      //   yref: 'paper',
      //   x: 1.04,
      //   xanchor: 'left',
      //   y: 0.5,
      //   yanchor: 'bottom',
      //   text: '%',
      //   font: {
      //     size: 25,

      //     //color: '#1F77B4',
      //   },
      //   showarrow: false,
      // },
    ],

    xaxis: {
      title: 'Weeks',
      autorange: true,
      type: 'category',
      tickangle: -45,
    },
    yaxis: {
      // title: {
      //   text: '#',
      //   font: {
      //     orientation: 'h',
      //     size: 25,
      //     color: '#203879',
      //   },
      // },
      // autorange: true,
      range: [0, null]
    },
    yaxis2: {
      title: {
        text: 'Statistical range of normal percentage*',
        font: {
          size: 25,
          color: '#BBBBBC',
        },
      },
      showgrid: false,
      titlefont: { color: 'rgb(148, 103, 189)' },
      tickfont: { color: 'rgb(148, 103, 189)' },
      overlaying: 'y',
      side: 'right',
      range: [0, null]
    },
    legend: {
      x: 0,
      y: -0.4,
      orientation: 'h',
    },
  }

  function handleDataChangeAge(value): void {
    if (value.length === 0) {
      setSelectedAgeClass(['0-200'])
    } else {
      setSelectedAgeClass(value)
    }
  }

  const handleDataChangeVirus = (value) => {
    setSelectedDiagnosisCategories(value)
  }

  return (
    <>
      <div className='row'>
        <label style={{ width: '10%' }} className='col-1 mt-3 text-primary fw-medium fs-5'>
          Age group :
        </label>
        <div className='col-6'>
          <Select
            variant='borderless'
            className='border border-gray-500 border-3 rounded'
            style={{ width: '71%' }}
            size='large'
            value={selectedAgeClass}
            onChange={(value) => handleDataChangeAge(value)}
            mode='multiple'
          >
            <Option key={'0-200'} value={'0-200'}>
              {'All ages'}
            </Option>
            <Option key={'Less than 1 year'} value={'Less than 1 year'}>
              {'Less than 1 year'}
            </Option>
            <Option key={'[1 - 5[ year(s)'} value={'[1 - 5[ year(s)'}>
              {'1 to 5 years'}
            </Option>
            <Option key={'[5 - 20[ year(s)'} value={'[5 - 20[ years'}>
              {'5 to 19 years'}
            </Option>
            <Option key={'[20 - 50[ year(s)'} value={'[20 - 50[ years'}>
              {'20 to 49 years'}
            </Option>
            <Option key={'[50 - 65[ year(s)'} value={'[50 - 65[ years'}>
              {'50 to 64 years'}
            </Option>
            <Option key={'65 years and older'} value={'65 years and older'}>
              {'65 years and older'}
            </Option>
          </Select>
        </div>
        <div className='row mt-2'>
          <label style={{ width: '10%' }} className='col-1 mt-3 text-primary fw-medium fs-5'>
            Virus :
          </label>
          <div className='col-6'>
            <Select
              variant='borderless'
              className='border border-gray-500 border-3 rounded'
              style={{ width: '72.5%' }}
              size='large'
              value={selectedDiagnosisCategories}
              onChange={(value, option) => handleDataChangeVirus(value)}
            >
              <Option key={'all_resp_diags'} value={'all_resp_diags'}>
                {'All virus'}
              </Option>
              <Option key={'FLU'} value={'FLU'}>
                {'Influenza'}
              </Option>
              <Option key={'COVID-19'} value={'COVID-19'}>
                {'SARS-CoV-2'}
              </Option>
              <Option key={'RSV'} value={'RSV'}>
                {'RSV'}
              </Option>
              <Option key={'RI_OTHER_VIRUS'} value={'RI_OTHER_VIRUS'}>
                {'Other respiratory infections'}
              </Option>
            </Select>
          </div>
        </div>
      </div>
      <div className='plotContainer'>
        <Plot
          style={{ width: '100%', height: '400px' }}
          data={plotData}
          layout={layout}
          config={{
            displaylogo: false,
            responsive: true,
          }}
        />
      </div>
    </>
  )
}

export default StreamgraphChart
